<template>
  <div data-component="base-time-picker">
    <div class="base-time-picker-inputs">
      <or-input
        data-input="base-time-picker-hours"
        placeholder="00"
        :model-value="hoursAsString"
        :error="Boolean(error)"
        @input="onChangeHours"
      />

      <or-icon icon="go_to_line" />

      <or-input
        data-input="base-time-picker-minutes"
        placeholder="00"
        :error="Boolean(error)"
        :model-value="minutesAsString"
        @input="onChangeMinutes"
      />
    </div>

    <or-error v-if="error">
      {{ error }}
    </or-error>
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from 'vue-demi';
import { TranslateResult } from 'vue-i18n';
import {
  OrIconV3 as OrIcon,
  OrInputV3 as OrInput,
  OrErrorV3 as OrError,
} from '@onereach/ui-components-25';

export default defineComponent({
  name: 'BaseTimePicker',
  components: { OrIcon, OrInput, OrError },
  props: {
    hours: {
      type: Number,
      default: 0,
    },
    minutes: {
      type: Number,
      default: 0,
    },
    error: {
      type: String as PropType<TranslateResult>,
      default: '',
    },
  },
  setup() {
    return { NUMBER_REGEX: /^[0-9]*$/g };
  },
  computed: {
    hoursAsString() {
      return this.padValueWithZeros(this.hours);
    },
    minutesAsString() {
      return this.padValueWithZeros(this.minutes);
    },
  },
  methods: {
    onChangeHours(event: KeyboardEvent) {
      const MAX_HOURS = 23;
      const target = event.target as HTMLInputElement;
      const input = target.value;

      /* Allow only integer values */
      if (input && !new RegExp(this.NUMBER_REGEX).test(input)) {
        target.value = `${this.hours}`;
        return;
      }

      let hours = Number(input);
      if (hours > MAX_HOURS) {
        hours = MAX_HOURS;
        target.value = `${hours}`;
      }

      this.$emit('time-change', { hours, minutes: this.minutes });
    },

    onChangeMinutes(event: KeyboardEvent) {
      const MAX_MINUTES = 59;
      const target = event.target as HTMLInputElement;
      const input = target.value;

      /* Allow only integer values */
      if (input && !new RegExp(this.NUMBER_REGEX).test(input)) {
        target.value = `${this.minutes}`;
        return;
      }

      let minutes = Number(input);
      if (minutes > MAX_MINUTES) {
        minutes = MAX_MINUTES;
        target.value = `${minutes}`;
      }

      this.$emit('time-change', { hours: this.hours, minutes });
    },

    padValueWithZeros(value: string | number) {
      const ZERO_SYMBOL = '0';
      return _.padStart(String(value), 2, ZERO_SYMBOL);
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-time-picker'] {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--s-2);

  .base-time-picker-inputs {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: var(--s-2);
    color: var(--or-c-on-background);
    font-size: 1rem;

    .or-input-v3 {
      width: 5rem;

      ::v-deep input {
        text-align: center;
      }
    }
  }
}
</style>
